import React from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import {
  GET_CHECK_DETAILS_BY_TYPE,
} from "../../apis/settingsApis/framework-apis/frameworkApis";
import { GET_USER_DETAILS } from "../../apis/dashboard-apis/dashboardDetailApi";
import { CheckCategory } from "../../utils/enums";
import { useAuditContext } from "../../hooks/useAuditContext";
import Role from "../../utils/roles";
import { formatDateForInput, getEndOfDayUTC } from "../../utils/dateUtils";
import EvidenceResourcesTab from "./components/EvidenceResourcesTab";

const EvidenceDetailsPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const { checkType, auditId } = useParams();
  const audit = location.state?.audit;
  const resourcesRefetchRef = React.useRef(null);

  const isAuditorView = location.pathname.startsWith("/auditor/");

  // Get user role
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const userDetails = userData?.getAccountDetails?.userDetails || {};
  const isAuditor = userDetails?.roles?.some(
    (role) => role?.role_description === Role.AUDITOR
  );
  const userRole = isAuditor ? Role.AUDITOR : Role.ADMIN;

  // Get audit end date
  const { auditEndDate } = useAuditContext({
    auditId: audit?.id || auditId,
    userRole,
  });

  const {
    data,
    loading: checkLoading,
    error: checkError,
    refetch: refetchCheckDetails,
  } = useQuery(GET_CHECK_DETAILS_BY_TYPE, {
    variables: {
      check_type: checkType,
      audit_id: audit?.id || auditId,
      end_time: auditEndDate,
    },
    skip: !checkType || (isAuditorView && !(audit?.id || auditId)),
    fetchPolicy: "network-only",
  });

  const checkDetails = data?.getCheckDetailsByType;
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;

  const checkCategoryLower =
    checkDetails?.active_check_category?.toLowerCase() || "";

  const checkCategory = React.useMemo(() => {
    if (!checkCategoryLower) return null;
    if (checkCategoryLower.includes(CheckCategory?.AZURE_SYSTEM_CHECK))
      return CheckCategory?.AZURE_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.AWS_SYSTEM_CHECK))
      return CheckCategory?.AWS_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.GCP_SYSTEM_CHECK))
      return CheckCategory?.GCP_SYSTEM_CHECK;
    if (checkCategoryLower.includes(CheckCategory?.PEOPLE_CHECK))
      return CheckCategory?.PEOPLE_CHECK;
    return null;
  }, [checkCategoryLower]);

  const isLoading = checkLoading || !checkDetails;

  if (!checkType || checkError || !checkDetails) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">
          Error loading check
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {checkError?.message || "Check not found"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.customColors.background.header,
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        overflowY: "auto",
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: `1px solid ${theme.customColors.border.main}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            p: 3,
            minHeight: "calc(100vh - 250px)",
          }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height={60}
            sx={{ mb: 2, borderRadius: "8px" }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height="calc(100vh - 400px)"
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      ) : (
        <EvidenceResourcesTab
          checkDetails={checkDetails}
          activeCheckId={activeCheckId}
          checkCategory={checkCategory}
          onRefetch={refetchCheckDetails}
          onRegisterRefetch={(refetchFn) => {
            resourcesRefetchRef.current = refetchFn;
          }}
          isAuditorView={isAuditorView}
        />
      )}
    </Box>
  );
};

export default EvidenceDetailsPage;
